import { useInfiniteQuery } from "@tanstack/react-query";
import { apiFetch, type Page } from "./client";

/** One line of who-changed-what (K-37). */
export interface AuditEntry {
  id: string;
  entity_type: "transaction" | "transfer" | "account" | "budget" | "category";
  entity_id: string;
  action: "create" | "update" | "delete" | "restore";
  diff?: Record<string, { before?: unknown; after?: unknown }>;
  occurred_at: string;
  actor?: { user_id: string; display_name: string };
  actor_user_id?: string;
}

/**
 * The marker a free-text field's value is replaced by.
 *
 * The API never sends a household's own words here — a note is recorded as
 * having changed, never as what it changed to (see `domain.Auditable`). The
 * screen renders this as a phrase rather than as the marker itself.
 */
export const REDACTED = "«redacted»";

export const auditQueryKey = (householdId: string) =>
  ["households", householdId, "audit"] as const;

/**
 * The household's history, newest first.
 *
 * Paged rather than fetched whole: this table only grows, and a household that
 * has been using Kontora for a year has more history than a screen needs.
 */
export function useAudit(householdId: string | undefined) {
  return useInfiniteQuery({
    queryKey: auditQueryKey(householdId ?? ""),
    enabled: Boolean(householdId),
    initialPageParam: "",
    queryFn: ({ pageParam, signal }) =>
      apiFetch<Page<AuditEntry>>(
        `/api/v1/households/${householdId}/audit?limit=25` +
          (pageParam ? `&cursor=${encodeURIComponent(String(pageParam))}` : ""),
        { signal },
      ),
    getNextPageParam: (last) => last.pagination.next_cursor ?? undefined,
  });
}
